import moment, { Moment } from 'moment';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { DashboardState } from '../models';
import { Defect } from '../models/defects';
import { getNewDateDueBasedOnInterval } from '../components/DefectDrawer/components/utils';

interface UseDefectDisplayDateProps {
  defect: Defect;
  ignoreDatetimeDue: boolean;
}

const useDefectDisplayDate = ({ defect, ignoreDatetimeDue }: UseDefectDisplayDateProps): string => {
  const {
    userSettings: { details, dateFormat },
    aircraft: { aircraftMap },
  } = useSelector((state: DashboardState) => state);

  const { id } = useParams<{ id: string }>();

  const getEndOfDay = (date?: string) => {
    const baseDate = date ? moment.utc(date) : moment.utc();
    return baseDate.startOf('day').add(23, 'hours').add(59, 'minutes');
  };

  const formatDefectDate = (date: Moment): string => date.format(`${dateFormat} HH:mm`);

  const utcOffsetSeconds =
    details?.operators?.find((op) => op.id === aircraftMap.get(id)?.operator_id)?.operator_setting
      ?.utc_offset_seconds || 0;

  const calculateNewDate = (baseDate: Moment): Moment => {
    return utcOffsetSeconds !== 0 ? baseDate.add(utcOffsetSeconds, 'seconds') : baseDate;
  };

  const handleCategoryADefect = (passedDefect: Defect): Moment => {
    const baseDate = getEndOfDay(
      moment.utc(passedDefect.raised_at).subtract(utcOffsetSeconds, 'seconds').toISOString());

    if (passedDefect.calendar_days_limit && passedDefect.seconds_limit) {
      const dateWithCalendarDaysLimit = calculateNewDate(baseDate.add(passedDefect.calendar_days_limit, 'days'));
      const dateWithSecondsLimit = moment.utc(passedDefect.raised_at).add(passedDefect.seconds_limit, 'seconds');
      return dateWithCalendarDaysLimit.isBefore(dateWithSecondsLimit)
        ? dateWithCalendarDaysLimit
        : dateWithSecondsLimit;
    }

    if (passedDefect.calendar_days_limit) {
      return calculateNewDate(baseDate.add(passedDefect.calendar_days_limit, 'days'));
    }

    if (passedDefect.seconds_limit) {
      return moment.utc(passedDefect.raised_at).add(passedDefect.seconds_limit, 'seconds');
    }

    return calculateNewDate(baseDate);
  };

  if (!defect) return '-';

  // handle datetime due
  if (defect?.datetime_due && !ignoreDatetimeDue) {
    return formatDefectDate(moment.utc(defect?.datetime_due));
  }

  // handle added or amended rectification extension
  if (defect?.hasRectificationExtension && defect?.display_data?.category !== 'A') {
    if (!defect?.rectificationExtensionDateDue) return '-';
    return formatDefectDate(
      calculateNewDate(getEndOfDay(moment.utc(defect?.rectificationExtensionDateDue).format('YYYY-MM-DD')))
    );
  }

  // handle category A
  if (defect?.display_data?.category === 'A') {
    return formatDefectDate(handleCategoryADefect(defect));
  }

  // handle default case
  const raisedAtAfterOffset = moment.utc(defect?.raised_at).subtract(utcOffsetSeconds, 'seconds').toISOString();
  const newDate = getNewDateDueBasedOnInterval(defect?.display_data?.category, raisedAtAfterOffset);
  return formatDefectDate(calculateNewDate(getEndOfDay(newDate)));
};

export default useDefectDisplayDate;
